import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Plus, Loader2, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { dueState } from "./TicketDue";
import { Avatar } from "./trello-ticket/TicketMembers";

const SCOPE = { internal: { label: "Interne", base: "/internal-tickets", cls: "bg-sky-100 text-sky-800" }, assistance: { label: "Assistance", base: "/tickets", cls: "bg-violet-100 text-violet-800" } };
const DUE_CLS = { overdue: "bg-orange-100 text-orange-800", soon: "bg-amber-100 text-amber-900", done: "bg-emerald-500 text-white", future: "bg-norea-surfaceHover text-norea-text" };

function NewTicket({ dossier, onCreated, onCancel }) {
  const ex = dossier.extraction || {};
  const [scope, setScope] = useState("internal");
  const [title, setTitle] = useState(`${[ex.nom, ex.prenom].filter(Boolean).join(" ")} - `);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post(SCOPE[scope].base, { title: title.trim(), dossier_ids: [dossier.id] });
      toast.success("Ticket créé et associé au dossier");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-3 p-3 rounded-md border border-norea-border bg-norea-bg space-y-2" data-testid="dossier-new-ticket-form">
      <div className="inline-flex rounded-md border border-norea-border p-0.5 bg-white">
        {Object.entries(SCOPE).map(([k, v]) => (
          <button key={k} onClick={() => setScope(k)} className={`px-3 py-1 text-xs font-medium rounded ${scope === k ? "bg-norea-primary text-white" : "text-norea-textMuted"}`} data-testid={`dossier-new-ticket-scope-${k}`}>{v.label}</button>
        ))}
      </div>
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} className="input-field text-sm" data-testid="dossier-new-ticket-title" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="btn-primary text-xs" data-testid="dossier-new-ticket-submit">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Créer</button>
        <button onClick={onCancel} className="btn-secondary text-xs">Annuler</button>
      </div>
    </div>
  );
}

export default function DossierTicketsCard({ dossier }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get(`/dossiers/${dossier.id}/tickets`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), [dossier.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="card-flat p-5" data-testid="dossier-tickets-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1.5"><Ticket className="w-3 h-3" /> Tickets liés {items && <span className="normal-case tracking-normal text-norea-textMuted">({items.length})</span>}</div>
        {!creating && <button onClick={() => setCreating(true)} className="btn-secondary text-xs" data-testid="dossier-new-ticket-btn"><Plus className="w-3 h-3" /> Nouveau ticket</button>}
      </div>
      {creating && <NewTicket dossier={dossier} onCreated={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
      {!items ? <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        : items.length === 0 ? <div className="text-xs text-norea-textMuted italic" data-testid="dossier-tickets-empty">Aucun ticket associé à ce dossier</div>
        : (
          <ul className="divide-y divide-norea-border">
            {items.map((t) => {
              const st = dueState(t);
              return (
                <li key={t.id}>
                  <Link to={t.url} className="flex items-center gap-3 py-2 px-1 rounded hover:bg-norea-surfaceHover" data-testid={`dossier-ticket-${t.id}`}>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SCOPE[t.scope].cls}`}>{SCOPE[t.scope].label}</span>
                    {t.due_done && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                    <span className="flex-1 min-w-0 text-sm font-medium truncate">{t.title}</span>
                    <span className="text-[11px] uppercase tracking-wide text-norea-textMuted truncate max-w-[10rem]">{t.status_label}</span>
                    {t.due_at && st && <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${DUE_CLS[st]}`}><Clock className="w-3 h-3" /> {new Date(t.due_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>}
                    <span className="flex -space-x-1.5">{t.members.map((m) => <span key={m} className="ring-2 ring-white rounded-full"><Avatar email={m} size="w-5 h-5 text-[8px]" /></span>)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
